import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, errMsg } from "../../lib/api";

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><label className="field-label">{label}</label>{children}</div>
);

export const UserDialog = ({ open, user, onClose, onSaved }) => {
  const isNew = !user;
  const [form, setForm] = useState({ name: "", email: "", role: "staff", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(user ? { name: user.name, email: user.email, role: user.role, password: "" } : { name: "", email: "", role: "staff", password: "" });
  }, [open, user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isNew) await api.post("/users", form);
      else await api.patch(`/users/${user.id}`, { name: form.name, email: form.email, role: form.role });
      toast.success(isNew ? "User created" : "User updated");
      onSaved();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white rounded-3xl" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-emerald-950">{isNew ? "Add user" : "Edit user"}</DialogTitle>
          <DialogDescription>{isNew ? "New devices for this user will need your approval." : user?.email}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Full name"><Input data-testid="user-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" /></Field>
          <Field label="Email"><Input data-testid="user-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl" /></Field>
          <Field label="Role">
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="staff" data-testid="user-role-option-staff">Feedback Taker (staff)</SelectItem>
                <SelectItem value="admin" data-testid="user-role-option-admin">Management / Admin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {isNew && <Field label="Password"><Input data-testid="user-password-input" type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 rounded-xl" /></Field>}
          <button data-testid="user-save-button" disabled={busy} className="w-full h-11 rounded-full bg-emerald-900 text-amber-50 font-semibold hover:bg-emerald-800 flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {isNew ? "Create user" : "Save changes"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const PasswordDialog = ({ user, onClose }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPw(""); }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/password`, { password: pw });
      toast.success(`Password updated for ${user.name}`);
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white rounded-3xl" data-testid="password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-emerald-950">Change password</DialogTitle>
          <DialogDescription>{user?.name} · {user?.email}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="New password"><Input data-testid="new-password-input" type="text" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 rounded-xl" /></Field>
          <button data-testid="password-save-button" disabled={busy} className="w-full h-11 rounded-full bg-emerald-900 text-amber-50 font-semibold hover:bg-emerald-800 flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
